// src/services/dashboardService.ts
import { apiService } from './api';
import type {
  ConversationsConvertedResponseSP,
  Dashboard,
  DashboardGroup,
  DashboardGroupConversations,
  GetSendToElements,
} from '../types/dashboard';

class DashboardGroupService
{
  /**
   * Elimina grupo
   */
  async deleteDashboardGroup(dashboardGroupId: number, socketConnectionId: string): Promise<void>
  {
    try
    {
      await apiService.get<void>(
        `/dashboardgroup/Delete?id=${dashboardGroupId}&socketConnectionId=${socketConnectionId}`,
      );
    }
    catch (error)
    {
      console.error('Error delete dashboard group:', error);
      throw error;
    }
  }

  /**
   * Guarda nuevo orden de los dashboards
   */
  async updateDashboardGroupPosition(
    dashboardGroups: DashboardGroupConversations[],
  ): Promise<void>
  {
    try
    {
      await apiService.put<void>('/dashboardgroup/updatedashboardgroupposition', dashboardGroups);
    }
    catch (error)
    {
      console.error('Error set new order of groups:', error);
      throw error;
    }
  }

  /**
   * Guarda el nuevo nombre del Grupo
   */
  async updateDashboardGroup(dashboardGroups: DashboardGroup): Promise<void>
  {
    try
    {
      await apiService.put<void>('/dashboardgroup/updatedashboardgroup', dashboardGroups);
    }
    catch (error)
    {
      console.error('Error set name dashboardGroup:', error);
      throw error;
    }
  }

  /**
   * Make column dynamic (set as default)
   * Returns updated IsDefault state for all columns in the dashboard
   */
  async SetGroupAsDefault(
    DashboardGroupId: number,
    DashboardId: number,
    SocketConnectionId: string,
  ): Promise<{ dashboardGroupId: number; isDefault: boolean }[]>
  {
    try
    {
      const request = {
        DashboardId: DashboardId,
        DashboardGroupId: DashboardGroupId,
        SocketConnectionId: SocketConnectionId,
      };
      const response = await apiService.put<{ dashboardGroupId: number; isDefault: boolean }[]>(
        `/dashboard/SetGroupAsDefault`,
        request,
      );
      return response ?? [];
    }
    catch (error)
    {
      console.error('Error setting group as default:', error);
      throw error;
    }
  }

  /**
   * Get Elements to send
   */
  async GetSendToElements(DashboardGroupId: number): Promise<GetSendToElements>
  {
    try
    {
      const response = await apiService.get<GetSendToElements>(
        `/DashboardGroup/GetSendToElements?dashboardGroupId=${DashboardGroupId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error delete dashboard group:', error);
      throw error;
    }
  }

  /**
   * Get Elements to send
   */
  async GetCompatibleItemsCountToSendTo(
    configValueId: number,
    DashboardGroupId: number,
    projectId: number | null,
  ): Promise<{ Count: number }>
  {
    try
    {
      const response = await apiService.get<{ Count: number }>(
        `/DashboardGroup/GetCompatibleItemsCountToSendTo?configValueId=${configValueId}&dashboardGroupId=${DashboardGroupId}&projectId=${projectId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error delete dashboard group:', error);
      throw error;
    }
  }

  /**
   * Validate if exist the cards in the new dashboard
   */
  async GetConversationsDuplicated(
    dashboardId: number,
    dashboardGroupId: number,
    conversationIds?: string,
  ): Promise<boolean>
  {
    try
    {
      const response = await apiService.get<boolean>(
        `/dashboard/GetConversationsDuplicated?dashboardId=${dashboardId}&dashboardGroupId=${dashboardGroupId}&conversationIds=${conversationIds ?? ''}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error GetConversationsDuplicated:', error);
      throw error;
    }
  }

  /**
   * Validate if exist the cards in the new dashboard
   */
  async AddDashboardGroupByParametersValidateCompatibleItems(
    dashboardId: number,
    dashboardGroupId: number,
    conversationId?: string,
    dashboardGroupIdSend?: number,
    includeAllConversations?: boolean,
    configValueId?: number,
    projectId?: number,
    ParentConversationId?: number,
    dashboardGroupOrigin?: number,
  ): Promise<Dashboard>
  {
    try
    {
      const response = await apiService.get<Dashboard>(
        `dashboard/AddDashboardGroupByParametersValidateCompatibleItems?dashboardId=${dashboardId}&dashboardGroupId=${dashboardGroupId}&conversationId=${conversationId || 0}&dashboardGroupIdSend=${dashboardGroupIdSend || 0}&includeAllConversations=${includeAllConversations || false}&configValueId=${configValueId || 0}&projectId=${projectId || 0}&ParentConversationId=${ParentConversationId || 0}&dashboardGroupOrigin=${dashboardGroupOrigin || 0}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error GetConversationsDuplicated:', error);
      throw error;
    }
  }

  /**
   * Get Item Type To Convert By Security
   */
  async GetItemTypeToConvertBySecurity(
    projectId?: number,
    conversationId?: number,
    conversationIds?: string,
  )
  {
    try
    {
      const response = await apiService.get<Dashboard>(
        `${
          conversationIds
            ? `menu/getItemTypeToConvertBySecurity?conversationIds=${conversationIds}`
            : `menu/getItemTypeToConvertBySecurity?projectId=${projectId}&conversationId=${conversationId}`
        }`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error GetConversationsDuplicated:', error);
      throw error;
    }
  }

  /**
   * Validate Conversation To Convert
   */
  async ValidateConversationToConvert(
    conversationId?: number,
    NewConfigValueId?: number,
  ): Promise<{ TypeErrorId: number; Message: string }>
  {
    try
    {
      const response = await apiService.get<{ TypeErrorId: number; Message: string }>(
        `Conversation/ValidateConversationToConvert?ConversationId=${conversationId}&NewConfigValueId=${NewConfigValueId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error GetConversationsDuplicated:', error);
      throw error;
    }
  }

  /**
   * Validate Conversation To Convert
   */
  async ConvertConversation(
    newConfigValueId: number,
    conversationList: string,
    convertHierarchy: boolean,
    dashboardGroupId: number,
    projectID: number,
    socketConnectionId: string,
  ): Promise<ConversationsConvertedResponseSP>
  {
    try
    {
      const response = await apiService.get<ConversationsConvertedResponseSP>(
        `Conversation/ConvertConversation?NewConfigValueId=${newConfigValueId}&ConversationList=${conversationList}&ConvertHierarchy=${convertHierarchy}&DashboardGroupId=${dashboardGroupId}&ProjectID=${projectID}&SocketConnectionId=${socketConnectionId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error GetConversationsDuplicated:', error);
      throw error;
    }
  }
}

export const dashboardGroupService = new DashboardGroupService();
